<template>
    <div>
        <h3 class="text-center">AUTORISATION PARENTALE</h3>
        <p class="text-center text-sm text-red-900 italic mb-4">SÉJOUR EN IMMERSION D'UN PARTICIPANT MINEUR</p>

        <!-- ================================================ -->
        <!-- 1. Participant et représentant légal              -->
        <!-- ================================================ -->
        <div class="bg-red-100 p-6 mb-4">
            <h4 class="font-bold text-red-900 text-lg mb-4">{{ __('ap_s1_titre') }}</h4>

            <!-- Je soussigné(e) -->
            <div class="inline-block w-full mb-4">
                <label class="text-sm block text-red-900 font-bold" for="soussignes_parents">
                    <span v-html="__('soussignes_parents')"></span>
                    <sup class="text-red-500">*</sup>
                </label>
                <input v-model="$parent.form.autorisation_parentale.soussignes_parents"
                    :class="{'border-red-500':$parent.form.errors.autorisation_parentale}"
                    class="w-full px-2 py-2 text-gray-700 rounded-sm border-cavilam-primary"
                    id="soussignes_parents" name="soussignes_parents" type="text" required>
                <p class="text-red-500 text-xs" :class="{'invisible':!$parent.form.errors.autorisation_parentale}">
                    {{ __($parent.form.errors.autorisation_parentale) }}
                </p>
            </div>

            <!-- Enfant : nom / nationalité -->
            <div class="flex gap-4 flex-col md:flex-row">
                <div class="inline-block mt-2 w-full md:w-1/2">
                    <label class="text-sm block text-red-900 font-bold" for="nom_enfant">
                        <span v-html="__('nom_enfant')"></span>
                        <sup class="text-red-500">*</sup>
                    </label>
                    <input v-model="$parent.form.autorisation_parentale.nom_enfant"
                        :class="{'border-red-500':$parent.form.errors.autorisation_parentale}"
                        class="w-full px-2 py-2 text-gray-700 rounded-sm border-cavilam-primary"
                        id="nom_enfant" name="nom_enfant" type="text" required>
                </div>
                <div class="inline-block mt-2 w-full md:w-1/2">
                    <label class="text-sm block text-red-900 font-bold" for="prenom_enfant">
                        <span v-html="__('prenom_enfant')"></span>
                        <sup class="text-red-500">*</sup>
                    </label>
                    <input v-model="$parent.form.autorisation_parentale.prenom_enfant"
                        :class="{'border-red-500':$parent.form.errors.autorisation_parentale}"
                        class="w-full px-2 py-2 text-gray-700 rounded-sm border-cavilam-primary"
                        id="prenom_enfant" name="prenom_enfant" type="text" required>
                </div>
            </div>

            <!-- Enfant : prénom / sexe -->
            <div class="flex gap-4 flex-col md:flex-row mt-2">
                <div class="inline-block mt-2 w-full md:w-1/2">
                    <label class="text-sm block text-red-900 font-bold" for="nationalite_enfant">
                        <span v-html="__('nationalite')"></span>
                        <sup class="text-red-500">*</sup>
                    </label>
                    <select v-model="$parent.form.autorisation_parentale.nationalite"
                        :class="{'border-red-500':$parent.form.errors.autorisation_parentale}"
                        class="w-full px-2 py-2 text-gray-700 rounded cursor-pointer border-cavilam-primary"
                        id="nationalite_enfant" name="nationalite" required>
                        <option :value="null"> -- {{ __('veuillez_selectionner_nationalite') }} -- </option>
                        <option v-for="pays in nationalites" :key="pays.code" :value="pays.code">{{ pays.libelle }}</option>
                    </select>
                </div>
                <div class="inline-block mt-2 w-full md:w-1/2">
                    <label class="text-sm block text-red-900 font-bold">
                        <span v-html="__('sexe_enfant')"></span>
                        <sup class="text-red-500">*</sup>
                    </label>
                    <div class="flex mt-2">
                        <label class="inline-flex items-center mx-2 cursor-pointer">
                            <input v-model="$parent.form.autorisation_parentale.sexe_enfant" type="radio" name="sexe_enfant" value="fille" class="cursor-pointer h-5 w-5 text-cavilam-primary border-cavilam-primary" required>
                            <span class="ml-2 text-red-900 font-bold">{{ __('fille') }}</span>
                        </label>
                        <label class="inline-flex items-center mx-2 cursor-pointer">
                            <input v-model="$parent.form.autorisation_parentale.sexe_enfant" type="radio" name="sexe_enfant" value="garcon" class="cursor-pointer h-5 w-5 text-cavilam-primary border-cavilam-primary" required>
                            <span class="ml-2 text-red-900 font-bold">{{ __('garcon') }}</span>
                        </label>
                    </div>
                </div>
            </div>

            <!-- Date de naissance -->
            <div class="mt-2 w-full md:w-1/2">
                <label class="block text-red-900 font-bold text-sm" for="date_naissance">
                    {{ __('date_naissance') }} <sup class="text-red-500">*</sup>
                </label>
                <input v-model="$parent.form.autorisation_parentale.date_naissance"
                    placeholder="YYYY-MM-DD" min="1900-01-01" :max="maxDate"
                    :class="{'border-red-500':$parent.form.errors.autorisation_parentale}"
                    class="cursor-pointer w-full px-2 py-2 text-gray-700 rounded border-cavilam-primary"
                    id="date_naissance" name="date_naissance" type="date" required
                    pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}">
            </div>

            <!-- Texte d'autorisation de participation -->
            <p class="mt-4 text-red-900 italic" v-html="__('ap_s1_participe')"></p>
        </div>

        <!-- ================================================ -->
        <!-- 2. Encadrement                                   -->
        <!-- ================================================ -->
        <div class="bg-red-100 p-6 mb-4">
            <h4 class="font-bold text-red-900 text-lg mb-2">{{ __('ap_s2_titre') }}</h4>
            <p class="text-red-900 text-sm" v-html="__('ap_s2_texte')"></p>
        </div>

        <!-- ================================================ -->
        <!-- 3. Hébergement en famille d'accueil              -->
        <!-- ================================================ -->
        <div class="bg-red-100 p-6 mb-4">
            <h4 class="font-bold text-red-900 text-lg mb-2">{{ __('ap_s3_titre') }}</h4>
            <p class="text-red-900 text-sm" v-html="__('ap_s3_texte')"></p>
        </div>

        <!-- ================================================ -->
        <!-- 4. Autorisation de sorties                       -->
        <!-- ================================================ -->
        <div class="bg-red-100 p-6 mb-4">
            <h4 class="font-bold text-red-900 text-lg mb-4">{{ __('autorisation_sorties_titre') }}</h4>
            <div class="flex flex-col gap-4">
                <label class="inline-flex items-start mx-2 cursor-pointer">
                    <input v-model="$parent.form.autorisation_parentale.autorisation_sorties" type="radio" name="autorisation_sorties" :value="true" class="cursor-pointer h-5 w-5 text-cavilam-primary border-cavilam-primary" required>
                    <span class="ml-2 text-red-900" v-html="__('autorise_sorties')"></span>
                </label>
                <label class="inline-flex items-center mx-2 cursor-pointer">
                    <input v-model="$parent.form.autorisation_parentale.autorisation_sorties" type="radio" name="autorisation_sorties" :value="false" class="cursor-pointer h-5 w-5 text-cavilam-primary border-cavilam-primary" required>
                    <span class="ml-2 text-red-900" v-html="__('autorise_sorties_non')"></span>
                </label>
            </div>
            <div class="mt-4">
                <label class="text-sm block text-red-900 font-bold" for="sorties_horaires">
                    <span v-html="__('sorties_horaires')"></span>
                </label>
                <textarea v-model="$parent.form.autorisation_parentale.sorties_horaires" name="sorties_horaires" id="sorties_horaires" class="w-full" rows="2"></textarea>
            </div>
            <p class="mt-3 text-red-900 text-xs italic" v-html="__('ap_s4_note')"></p>
        </div>

        <!-- ================================================ -->
        <!-- 5. Déplacements                                  -->
        <!-- ================================================ -->
        <div class="bg-red-100 p-6 mb-4">
            <h4 class="font-bold text-red-900 text-lg mb-2">{{ __('ap_s5_titre') }}</h4>
            <p class="text-red-900 text-sm" v-html="__('ap_s5_texte')"></p>
        </div>

        <!-- ================================================ -->
        <!-- 6. Assurance                                     -->
        <!-- ================================================ -->
        <div class="bg-red-100 p-6 mb-4">
            <h4 class="font-bold text-red-900 text-lg mb-2">{{ __('ap_s6_titre') }}</h4>
            <p class="text-red-900 text-sm" v-html="__('ap_s6_intro')"></p>
            <ul class="list-disc list-inside pl-4 mt-2 text-red-900 text-sm">
                <li v-html="__('ap_s6_item1')"></li>
                <li v-html="__('ap_s6_item2')"></li>
                <li v-html="__('ap_s6_item3')"></li>
            </ul>
        </div>

        <!-- ================================================ -->
        <!-- 7. Soins médicaux et contact d'urgence           -->
        <!-- ================================================ -->
        <div class="bg-red-100 p-6 mb-4">
            <h4 class="font-bold text-red-900 text-lg mb-2">{{ __('ap_s7_titre') }}</h4>
            <p class="text-red-900 text-sm" v-html="__('ap_s7_intro')"></p>
            <ul class="list-disc list-inside pl-4 mt-2 mb-4 text-red-900 text-sm">
                <li v-html="__('ap_s7_item1')"></li>
                <li v-html="__('ap_s7_item2')"></li>
                <li v-html="__('ap_s7_item3')"></li>
                <li v-html="__('ap_s7_item4')"></li>
            </ul>

            <!-- Informations médicales -->
            <div class="mt-4">
                <p class="text-red-900 font-bold">{{ __('info_sante_enfant') }}</p>
                <textarea v-model="$parent.form.autorisation_parentale.info_sante_enfant" name="info_sante_enfant" id="info_sante_enfant" class="w-full" rows="4"></textarea>
            </div>

            <!-- Contact d'urgence -->
            <div class="w-full h-0.5 bg-white mx-auto mt-6"></div>
            <p class="text-red-900 font-bold mt-4">{{ __('contact_urgence_titre') }}</p>
            <div class="mt-2">
                <label class="text-sm block text-red-900 font-bold" for="urgence_nom_lien">
                    <span v-html="__('urgence_nom_lien')"></span>
                    <sup class="text-red-500">*</sup>
                </label>
                <input v-model="$parent.form.renseignements_enfant_parent.urgence_nom_lien"
                    :class="{'border-red-500':$parent.form.errors.renseignements_enfant_parent}"
                    class="w-full px-2 py-2 text-gray-700 rounded-sm border-cavilam-primary"
                    id="urgence_nom_lien" name="urgence_nom_lien" type="text" required>
            </div>
            <div class="flex gap-4 mt-2">
                <div class="w-full md:w-1/2">
                    <label class="text-sm block text-red-900 font-bold" for="urgence_tel">
                        <span v-html="__('telephone')"></span>
                        <sup class="text-red-500">*</sup>
                    </label>
                    <input v-model="$parent.form.renseignements_enfant_parent.urgence_tel"
                        class="w-full px-2 py-2 text-gray-700 rounded-sm border-cavilam-primary"
                        id="urgence_tel" name="urgence_tel" type="text" required>
                </div>
                <div class="w-full md:w-1/2">
                    <label class="text-sm block text-red-900 font-bold" for="urgence_email">
                        <span v-html="__('adresse_email')"></span>
                    </label>
                    <input v-model="$parent.form.renseignements_enfant_parent.urgence_email"
                        class="w-full px-2 py-2 text-gray-700 rounded-sm border-cavilam-primary"
                        id="urgence_email" name="urgence_email" type="text">
                </div>
            </div>
        </div>

        <!-- ================================================ -->
        <!-- 8. Engagement du participant                     -->
        <!-- ================================================ -->
        <div class="bg-red-100 p-6 mb-4">
            <h4 class="font-bold text-red-900 text-lg mb-2">{{ __('ap_s8_titre') }}</h4>
            <p class="text-red-900 text-sm" v-html="__('ap_s8_intro')"></p>
            <ul class="list-disc list-inside pl-4 mt-2 text-red-900 text-sm">
                <li v-html="__('ap_s8_item1')"></li>
                <li v-html="__('ap_s8_item2')"></li>
                <li v-html="__('ap_s8_item3')"></li>
                <li v-html="__('ap_s8_item4')"></li>
                <li v-html="__('ap_s8_item5')"></li>
            </ul>
            <p class="mt-3 text-red-900 text-sm" v-html="__('ap_s8_sanctions')"></p>
        </div>

        <!-- ================================================ -->
        <!-- 9. Droit à l'image                               -->
        <!-- ================================================ -->
        <div class="bg-red-100 p-6 mb-4">
            <h4 class="font-bold text-red-900 text-lg mb-2">{{ __('ap_s9_titre') }}</h4>
            <p class="text-red-900 text-sm mb-4" v-html="useNosportConsentements ? __('autorisation_droit_image_nosport') : __('autorisation_droit_image')"></p>

            <!-- Autorisation photo -->
            <div class="flex flex-col gap-4 mb-4">
                <label class="inline-flex items-start mx-2 cursor-pointer">
                    <input v-model="$parent.form.autorisation_image.autorise" type="radio" name="autorise_photo" :value="true" class="cursor-pointer h-5 w-5 text-cavilam-primary border-cavilam-primary" required>
                    <span class="ml-2 text-red-900" v-html="useNosportConsentements ? __('autorise_photo_nosport') : __('autorise_photo')"></span>
                </label>
                <label class="inline-flex items-center mx-2 cursor-pointer">
                    <input v-model="$parent.form.autorisation_image.autorise" type="radio" name="autorise_photo" :value="false" class="cursor-pointer h-5 w-5 text-cavilam-primary border-cavilam-primary" required>
                    <span class="ml-2 text-red-900" v-html="__('autorise_photo_non')"></span>
                </label>
            </div>

            <!-- RGPD -->
            <p class="text-red-900 text-xs italic mb-4" v-html="__('ap_s9_rgpd')"></p>

            <!-- Fait à / Le -->
            <div class="flex gap-4">
                <div class="w-full md:w-1/2">
                    <label class="text-sm block text-red-900 font-bold" for="fait_a">
                        <span v-html="__('fait_a')"></span>
                        <sup class="text-red-500">*</sup>
                    </label>
                    <input v-model="$parent.form.autorisation_parentale.fait_a"
                        :class="{'border-red-500':$parent.form.errors.autorisation_parentale}"
                        class="w-full px-2 py-2 text-gray-700 rounded-sm border-cavilam-primary"
                        id="fait_a" name="fait_a" type="text" required>
                </div>
                <div class="w-full md:w-1/2">
                    <label class="block text-red-900 font-bold text-sm" for="fait_le">
                        {{ __('fait_le') }} <sup class="text-red-500">*</sup>
                    </label>
                    <input v-model="$parent.form.autorisation_parentale.fait_le"
                        placeholder="YYYY-MM-DD" min="1900-01-01" :max="maxDate"
                        :class="{'border-red-500':$parent.form.errors.fait_le}"
                        class="cursor-pointer w-full px-2 py-2 text-gray-700 rounded border-cavilam-primary"
                        id="fait_le" name="fait_le" type="date" required
                        pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}">
                    <span class="text-gray-500 italic text-xs">{{ __('format_date_jma') }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import InputForm from "@/Components/InputForm";
export default {
    name: "AutorisationParentale",
    components: {
        InputForm,
    },
    props: {
        useNosportConsentements: {
            type: Boolean,
            default: false,
        },
        nationalites: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            form: this.$parent.form,
        }
    },
    computed: {
        maxDate() {
            const d = new Date();
            const pad = (n) => String(n).padStart(2, '0');
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        },
    },
}
</script>
